// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link from "@insite/mobius/Link";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import { SelectComponentProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    appendLinkElement,
    getCanonical,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
} from "@insite/mobius/utilities/linkElementsManipulation";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Tooltip from "@werner/MobiusOverrides/Tooltip/Tooltip";
import { color, styledLinksMenu, styledScrollbar, xScreen } from "@werner/XDL/Utilities";
import { xRegex } from "@werner/XDL/Utilities/validation";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const ENTER_KEY = "Enter";

export interface PaginationPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** String describing the URL of the previous page in the series */
        prevPage?: string;
        /** String describing the the URL of the next page in the series */
        nextPage?: string;
        // XNG-Changes: Add ability to show one of pagination items or both
        showResultsPerPage?: boolean;
        showPagesNumber?: boolean;
        justifyContent?: "flex-start" | "center" | "flex-end";
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const responsiveStyles = css`
    margin: 15px 0;
    width: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
`;

const iconButtonStyles = css`
    width: 24px;
    border-radius: 4px;
    justify-content: center;
`;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                css`
                    flex-wrap: nowrap;
                `,
                null,
                null,
            ],
            "max",
        )};
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    margin: 4px 0;
                `,
                null,
                null,
                css`
                    margin: 16px 0;
                `,
                null,
            ],
            "min",
        )};
    display: flex;
    align-items: center;
    ${({ justifyContent }: any) => justifyContent && `justify-content: ${justifyContent};`}
    flex-wrap: wrap;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, responsiveStyles, null, null], "max")};
    ul {
        list-style: none;
        display: flex;
        align-items: center;
    }
    li {
        display: inline-block;
        margin-right: 5px;
        &:last-child {
            margin-right: 0;
        }
    }
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, responsiveStyles, null, null], "max")};
    ${injectCss}
`;

const Ellipsis = styled.li<InjectableCss>`
    ${injectCss}
`;

// XNG-Changes
const styles = {
    xngPaginationElement: {
        inputWrapper: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
        perPageinputWrapper: {
            css: css`
                display: flex;
                align-items: center;
                ${xScreen("lte", "md")} {
                    width: 75px;
                    white-space: nowrap;
                }
            `,
        },
        labelWrapper: {
            css: css`
                display: flex;
                align-items: center;
                gap: 4px;
            `,
        },
        messageLabel: {
            size: 15,
            color: "common.ui3",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        messageValue: {
            size: 15,
            color: "common.ui3",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        perPageLabel: {
            size: 15,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        messageError: {
            size: 12,
            color: "danger",
            css: css`
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                text-align: center;
            `,
        },
    },
    pageLink: {
        typographyProps: {
            weight: 400,
        },
        css: css`
            ${TypographyStyle} {
                color: ${color("text")};
            }
        `,
    },
    tooltip: {
        tooltipContent: {
            css: css`
                padding: 0;
                & > div {
                    ${styledLinksMenu(180)}
                    ${styledScrollbar(4)}
                }
            `,
        },
    },
};

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        mergeCss,
        prevPage,
        nextPage,
        // XNG-Changes:
        showResultsPerPage = true,
        showPagesNumber = true,
        justifyContent,
        ...otherProps
    } = props;

    React.useEffect(() => {
        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
            prevPage,
            nextPage,
            convertToNumberIfString(possibleStringCurrentPage),
            "",
        );
        const prevCanonical = `${getCanonical()}${prevQueryString}`;
        const nextCanonical = `${getCanonical()}${nextQueryString}`;
        updateCanonical(currentQueryString);
        const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
        const prevNotFromProducts = prevPage?.indexOf("/api/v2/products?") < 0 && prevPage?.indexOf("search=") < 0;
        const nextNotFromProducts = nextPage?.indexOf("/api/v2/products?") < 0 && nextPage?.indexOf("search=") < 0;

        if (prevPage && !hasPrev && prevNotFromProducts) {
            appendLinkElement("prev", prevCanonical);
        }
        if (nextPage && !hasNext && nextNotFromProducts) {
            appendLinkElement("next", nextCanonical);
        }
        return () => {
            const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
            if (prevPage && hasPrev && prevNotFromProducts) {
                removeLinkElement("prev", prevCanonical);
            }
            if (nextPage && hasNext && nextNotFromProducts) {
                removeLinkElement("next", nextCanonical);
            }
        };
    }, [prevPage, nextPage]);

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);
    // XNG-Changes
    const [newPageState, setNewPageState] = useState(`${currentPage}`);
    const tooltipRef = useRef<any>(null);
    const resultsPerPageTooltipRef = useRef<any>(null);

    // <XNG-Changes>
    useEffect(() => {
        setNewPageState(`${currentPage}`);
    }, [possibleStringCurrentPage]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    }, [possibleStringCurrentPage, possibleStringResultsPerPage]);
    // </XNG-Changes>

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
        event.currentTarget.blur();
    };

    const { spreadProps, applyProp, applyStyledProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const navIconsSrc = spreadProps("navIconsSrc");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.pagination?.defaultProps?.mergeCss;

    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    let pagesToDisplay: ("e" | number)[] = [];
    if (finalPageIndex < 6) {
        for (let i = 1; i <= finalPageIndex; i += 1) {
            pagesToDisplay.push(i);
        }
    } else if (currentPage < 4) {
        pagesToDisplay = [1, 2, 3, 4, "e", finalPageIndex];
    } else if (currentPage >= 4 && currentPage < finalPageIndex - 2) {
        pagesToDisplay = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
    } else {
        pagesToDisplay = [
            "e",
            finalPageIndex - 4,
            finalPageIndex - 3,
            finalPageIndex - 2,
            finalPageIndex - 1,
            finalPageIndex,
        ];
    }

    const paginationButtons = pagesToDisplay.map(page => {
        if (page === "e") {
            return (
                <Ellipsis key={page} css={applyStyledProp(["cssOverrides", "ellipsis"], resolvedMergeCss)}>
                    &hellip;
                </Ellipsis>
            );
        }
        return (
            <li key={`page${page}`}>
                <Button
                    {...buttonDisplayProps(props, {
                        moreCss: numericButtonStyles,
                        page,
                        cssOverrides,
                        buttonProps,
                        currentPageButtonVariant,
                    })}
                    type="button"
                    aria-label={`${page.toString()}`}
                >
                    {page.toString()}
                </Button>
            </li>
        );
    });

    /** <XNG-Changes> : add Tooltip and TextField */
    const pagesList = [];
    for (let pageNumber = 1; pageNumber <= finalPageIndex; pageNumber++) {
        if (pageNumber === currentPage) {
            continue;
        }

        pagesList.push(
            <Link
                key={uniqueId()}
                onClick={() => {
                    onChangePage(pageNumber);
                    tooltipRef.current?.hide();
                }}
                {...styles.pageLink}
                aria-label={`${translate("Page")} ${pageNumber}`}
            >
                <Typography color="text">
                    {translate("Page")} {pageNumber}
                </Typography>
            </Link>,
        );
    }

    const onChangePageTextFieldHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        let transformedValue = value;
        if (value) {
            transformedValue = value.replace(xRegex.numbers, "") || "1";
            const newPageNumber = convertToNumberIfString(transformedValue);
            if (newPageNumber <= 0) {
                transformedValue = "";
            }
            if (newPageNumber > finalPageIndex) {
                transformedValue = `${currentPage || 1}`;
            }
        }
        setNewPageState(transformedValue);
    };

    const handleChangePageKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ENTER_KEY) {
            updatePageTextField();
        }
    };
    const updatePageTextField = () => {
        if (!newPageState) {
            setNewPageState(`${currentPage}`);
        }
        onChangePage(convertToNumberIfString(newPageState) || currentPage);
    };

    const xngPaginationElement = (
        <li>
            <StyledWrapper {...styles.xngPaginationElement.inputWrapper}>
                <Tooltip
                    text={pagesList}
                    trigger="hover"
                    extendedStyles={styles.tooltip}
                    placement="bottom-start"
                    triggerComponent={
                        <StyledWrapper {...styles.xngPaginationElement.labelWrapper}>
                            <Typography {...styles.xngPaginationElement.messageLabel}>
                                {translate(
                                    finalPageIndex
                                        ? `Page ${newPageState} of ${finalPageIndex}`
                                        : `Page ${newPageState}`,
                                )}
                            </Typography>
                            <Icon src={ChevronDown} size={14} color="common.ui3" />
                        </StyledWrapper>
                    }
                />
            </StyledWrapper>
        </li>
    );
    /** </XNG-Changes> */

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navIcon = (
        pageIndex: number,
        icon: string | React.ComponentType,
        ariaLabel: string,
        disabledFlag: boolean,
        dataTestSelector?: string,
    ) => (
        <Clickable
            variant="secondary"
            {...buttonDisplayProps(props, {
                page: -1,
                moreCss: iconButtonStyles,
                cssOverrides,
                buttonProps,
                currentPageButtonVariant,
            })}
            aria-label={ariaLabel}
            disabled={disabledFlag}
            onClick={() => {
                onChangePage(pageIndex);
            }}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
            type="button"
        >
            <VisuallyHidden>{ariaLabel}</VisuallyHidden>
            <ButtonIcon size={14} src={icon} color="common.ui3" />
        </Clickable>
    );

    return (
        <PaginationStyle
            css={applyStyledProp(["cssOverrides", "pagination"], resolvedMergeCss)}
            {...omitSingle(otherProps, "cssOverrides")}
            {...{ justifyContent }}
        >
            {showResultsPerPage && resultsPerPageOptions?.length > 0 && (
                // <PerPageSelect css={applyStyledProp(["cssOverrides", "perPageSelect"], resolvedMergeCss)>
                //     <Select
                //         onChange={changeResultsPerPage}
                //         label={resultsPerPageLabel || translate("Results Per Page")}
                //         labelPosition="left"
                //         preventEllipsis={true}
                //         value={resultsPerPage}
                //         {...spreadProps("selectProps" as any)}
                //         data-test-selector="pagination_resultsPerPage"
                //     >
                //         {resultsPerPageOptions.map((n: string) => (
                //             <option key={n} value={n}>
                //                 {n}
                //             </option>
                //         ))}
                //     </Select>
                // </PerPageSelect>
                <StyledWrapper {...styles.xngPaginationElement.perPageinputWrapper}>
                    <Tooltip
                        text={resultsPerPageOptions.map((n: string) => (
                            <Link
                                key={n}
                                onClick={() => {
                                    changeResultsPerPage({ target: { value: n }, currentTarget: { value: n } } as any);
                                    resultsPerPageTooltipRef.current?.hide();
                                }}
                                {...styles.pageLink}
                                aria-label={`${n}`}
                            >
                                <Typography color="text">{n}</Typography>
                            </Link>
                        ))}
                        trigger="hover"
                        extendedStyles={styles.tooltip}
                        placement="bottom-start"
                        triggerComponent={
                            <StyledWrapper {...styles.xngPaginationElement.labelWrapper}>
                                <Typography {...styles.xngPaginationElement.perPageLabel}>
                                    {`${resultsPerPageLabel || translate("Per Page")}: `}
                                </Typography>
                                <Typography {...styles.xngPaginationElement.messageValue}>{resultsPerPage}</Typography>
                                <Icon src={ChevronDown} size={14} color="common.ui3" />
                            </StyledWrapper>
                        }
                    />
                </StyledWrapper>
            )}
            {showPagesNumber && (
                <LinkList
                    role="navigation"
                    aria-label={translate("Pagination navigation")}
                    css={applyStyledProp(["cssOverrides", "linkList"], resolvedMergeCss)}
                    data-test-selector={`pagination_currentPage_${currentPage}`}
                >
                    <ul>
                        {/* <li>
                        {navIcon(
                            1,
                            navIconsSrc.firstPage,
                            translate("First page"),
                            backDisabled,
                            "pagination_firstButton",
                        )}
                    </li> */}
                        <li>
                            {navIcon(
                                currentPage - 1,
                                navIconsSrc.previousPage,
                                translate("Previous page"),
                                backDisabled,
                                "pagination_previousButton",
                            )}
                        </li>
                        {xngPaginationElement}
                        <li>
                            {navIcon(
                                currentPage + 1,
                                navIconsSrc.nextPage,
                                translate("Next page"),
                                forwardDisabled,
                                "pagination_nextButton",
                            )}
                        </li>
                        {/* <li>
                        {navIcon(
                            finalPageIndex,
                            navIconsSrc.lastPage,
                            translate("Last page"),
                            forwardDisabled,
                            "pagination_lastButton",
                        )}
                    </li> */}
                    </ul>
                </LinkList>
            )}
        </PaginationStyle>
    );
});

/** @component */
export default withTheme(Pagination) as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
